<script lang="ts">
  import { bootTimings, type BootTimings } from '$lib/boot-timing';
  import { allContacts, saveCursor } from '$lib/db/contacts';
  import { db, type ContactRow } from '$lib/db/db';
  import { formatBytes, formatDateTime, formatPhone } from '$lib/format';
  import { t } from '$lib/i18n/index.svelte';
  import { directoryStatus, type DirectoryStatus } from '$lib/directory/directory-repo';
  import { offlineCopy, repairOfflineCopy, type OfflineCopy } from '$lib/offline-copy';
  import { syncContact } from '$lib/protocol/events-loop';
  import { currentSubscription, pushSupported, webPushGateway } from '$lib/push/push';
  import { live } from '$lib/state/live';

  import { goBack } from '../../app/router.svelte';

  const contacts = live<ContactRow[]>(() => allContacts(), []);

  // Read once, not reactively: these describe the launch that is already over.
  const boot: BootTimings = bootTimings();
  const ms = (value: number | null): string => (value === null ? '—' : `${value} ms`);

  let directory = $state<DirectoryStatus | undefined>(undefined);
  let offline = $state<OfflineCopy | undefined>(undefined);
  let repairing = $state(false);
  let persisted = $state<boolean | undefined>(undefined);
  let estimate = $state<{ used: number; quota: number } | undefined>(undefined);
  let gaps = $state<Record<string, boolean>>({});
  let resyncing = $state<Record<string, boolean>>({});
  let pushInfo = $state<
    { supported: boolean; permission: string; subscribed: boolean; vapidInManifest: boolean } | undefined
  >(undefined);

  $effect(() => {
    void directoryStatus().then((status) => (directory = status));
    void offlineCopy()
      .then((copy) => (offline = copy))
      .catch(() => undefined);
    void (async () => {
      const supported = pushSupported();
      pushInfo = {
        supported,
        permission: supported ? Notification.permission : '—',
        subscribed: supported && (await currentSubscription()) !== null,
        vapidInManifest: (await webPushGateway())?.vapidKey !== undefined,
      };
    })();
    void navigator.storage
      ?.persisted()
      .then((value) => (persisted = value))
      .catch(() => undefined);
    void navigator.storage
      ?.estimate()
      .then((value) => (estimate = { used: value.usage ?? 0, quota: value.quota ?? 0 }))
      .catch(() => undefined);
  });

  // Gap check: the per-thread sequence is monotonic and gapless, so a range that
  // holds fewer events than its width has holes. Detected and shown — the fix is
  // the user's explicit resync, exactly like the reference client.
  $effect(() => {
    void (async () => {
      const found: Record<string, boolean> = {};
      for (const contact of $contacts) {
        const rows = await db.events
          .where('[accountId+sequence]')
          .between([contact.accountId, -Infinity], [contact.accountId, Infinity])
          .toArray();
        if (rows.length === 0) {
          continue;
        }
        const sequences = rows.map((r) => r.sequence);
        const min = Math.min(...sequences);
        const max = Math.max(...sequences);
        found[contact.accountId] = max - min + 1 !== rows.length;
      }
      gaps = found;
    })();
  });

  async function repair(): Promise<void> {
    repairing = true;
    try {
      await repairOfflineCopy();
      // The replacement worker cannot control a page that started without it.
      location.reload();
    } catch (error) {
      console.error('[offline] repair failed', error);
      repairing = false;
    }
  }

  async function resync(accountId: string): Promise<void> {
    resyncing = { ...resyncing, [accountId]: true };
    const contact = $contacts.find((c) => c.accountId === accountId);
    // Refetch from the very beginning; event writes are idempotent on id, so
    // this fills holes without duplicating anything.
    await saveCursor(accountId, undefined, contact?.lastSequence ?? 0);
    await syncContact(accountId);
    resyncing = { ...resyncing, [accountId]: false };
  }
</script>

<header class="bar">
  <button class="back" onclick={goBack} aria-label={t('common.back')}>‹</button>
  <h1>{t('diag.title')}</h1>
</header>

<div class="body">
  <section>
    <h3>{t('diag.directory')}</h3>
    {#if directory !== undefined}
      <p class="row">
        <span>{t('diag.manifestVersion')}</span><span>{directory.manifest?.version ?? '—'}</span>
      </p>
      <p class="row">
        <span>{t('diag.fetchedAt')}</span>
        <span>{directory.fetchedAtMs !== undefined ? formatDateTime(new Date(directory.fetchedAtMs)) : '—'}</span>
      </p>
      <p class="row"><span>{t('diag.fromDomain')}</span><span>{directory.fromDomain ?? '—'}</span></p>
      <p class="row">
        <span>{t('diag.validUntil')}</span>
        <span>
          {directory.manifest !== undefined ? formatDateTime(directory.manifest.validUntil) : '—'}
        </span>
      </p>
      <p class="row"><span>{t('diag.entries')}</span><span>{directory.cachedEntries}</span></p>
      {#if directory.lastError !== undefined}
        <p class="row error"><span>{t('diag.lastError')}</span><span>{directory.lastError}</span></p>
      {/if}
    {/if}
  </section>

  <section>
    <h3>{t('diag.push')}</h3>
    {#if pushInfo !== undefined}
      <p class="row">
        <span>{t('diag.push.supported')}</span>
        <span>{pushInfo.supported ? t('diag.yes') : t('diag.no')}</span>
      </p>
      <p class="row"><span>{t('diag.push.permission')}</span><span>{pushInfo.permission}</span></p>
      <p class="row">
        <span>{t('diag.push.subscribed')}</span>
        <span>{pushInfo.subscribed ? t('diag.yes') : t('diag.no')}</span>
      </p>
      <p class="row">
        <span>{t('diag.push.vapid')}</span>
        <span>{pushInfo.vapidInManifest ? t('diag.yes') : t('diag.no')}</span>
      </p>
    {/if}
  </section>

  <section>
    <h3>{t('diag.offline')}</h3>
    {#if offline !== undefined}
      <p class="row">
        <span>{t('diag.offline.registered')}</span>
        <span>{offline.registered ? t('diag.yes') : t('diag.no')}</span>
      </p>
      <!-- The one that decides whether an offline launch works at all. -->
      <p class="row" class:error={!offline.controlling}>
        <span>{t('diag.offline.controlling')}</span>
        <span>{offline.controlling ? t('diag.yes') : t('diag.no')}</span>
      </p>
      <p class="row"><span>{t('diag.offline.state')}</span><span>{offline.state ?? '—'}</span></p>
      <p class="row" class:error={offline.files === 0}>
        <span>{t('diag.offline.files')}</span><span>{offline.files}</span>
      </p>
      <p class="row" class:error={!offline.startPage}>
        <span>{t('diag.offline.startPage')}</span>
        <span>{offline.startPage ? t('diag.yes') : t('diag.no')}</span>
      </p>
      <p class="row">
        <span>{t('diag.offline.servedLaunch')}</span>
        <span>{boot.serviceWorkerMs === null ? t('diag.no') : t('diag.yes')}</span>
      </p>
      <p class="note">{t('diag.offline.note')}</p>
      <button disabled={repairing} onclick={() => void repair()}>
        {repairing ? '…' : t('diag.offline.repair')}
      </button>
    {/if}
  </section>

  <section>
    <h3>{t('diag.storage')}</h3>
    <p class="row">
      <span>{t('diag.persisted')}</span>
      <span>{persisted === undefined ? '—' : persisted ? t('diag.persisted.yes') : t('diag.persisted.no')}</span>
    </p>
    {#if estimate !== undefined}
      <p class="row">
        <span></span>
        <span>{t('diag.estimate', { used: formatBytes(estimate.used), quota: formatBytes(estimate.quota) })}</span>
      </p>
    {/if}
    <p class="note">{t('diag.recovery')}</p>
  </section>

  <section>
    <h3>{t('diag.boot')}</h3>
    <p class="row"><span>{t('diag.boot.sw')}</span><span>{ms(boot.serviceWorkerMs)}</span></p>
    <p class="row"><span>{t('diag.boot.ttfb')}</span><span>{ms(boot.ttfbMs)}</span></p>
    <p class="row"><span>{t('diag.boot.entry')}</span><span>{ms(boot.entryMs)}</span></p>
    <p class="row"><span>{t('diag.boot.settings')}</span><span>{ms(boot.settingsMs)}</span></p>
    <p class="row"><span>{t('diag.boot.mounted')}</span><span>{ms(boot.mountedMs)}</span></p>
    <p class="note">{t('diag.boot.note')}</p>
  </section>

  <section>
    <h3>{t('diag.contacts')}</h3>
    {#each $contacts as contact (contact.accountId)}
      <div class="contact">
        <strong>{contact.name.length > 0 ? contact.name : formatPhone(contact.phone)}</strong>
        <p class="row mono"><span>{t('diag.cursor')}</span><span>{contact.cursor ?? '—'}</span></p>
        <p class="row"><span>{t('diag.lastSequence')}</span><span>{contact.lastSequence}</span></p>
        {#if gaps[contact.accountId] === true}
          <p class="row error"><span>{t('diag.gap')}</span></p>
        {/if}
        <button
          disabled={resyncing[contact.accountId] === true}
          onclick={() => void resync(contact.accountId)}
        >
          {resyncing[contact.accountId] === true ? '…' : t('diag.resync')}
        </button>
      </div>
    {/each}
  </section>
</div>

<style>
  .bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 12px;
  }
  .back {
    border: 0;
    background: transparent;
    font-size: 28px;
    cursor: pointer;
    color: var(--ink);
    padding: 0 8px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
  }
  .body {
    padding: 0 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
  h3 {
    margin: 0 0 8px;
    font-size: 15px;
    color: var(--ink-soft);
  }
  .row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin: 4px 0;
    font-size: 14px;
  }
  .row.mono span:last-child {
    font-family: ui-monospace, monospace;
    font-size: 12px;
    overflow-wrap: anywhere;
    text-align: right;
  }
  .row.error {
    color: var(--danger);
  }
  .note {
    font-size: 13px;
    color: var(--ink-soft);
  }
  .contact {
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 10px 12px;
    margin-bottom: 10px;
  }
  .contact button {
    margin-top: 6px;
    border: 1px solid var(--line);
    background: var(--surface);
    color: var(--ink);
    border-radius: 999px;
    padding: 6px 14px;
    cursor: pointer;
    font-size: 13px;
  }
</style>
